import { supabase } from './supabase';

/**
 * Phase 2+3: the billing source of truth is `profiles.plan` (user-level).
 * The Stripe webhook also syncs it onto `teams.plan` for every team the
 * user owns — that copy is what the backend enforces against and the only
 * one every team member can read under RLS (profiles are self-read-only).
 * So: own-plan lookups read profiles (getUserPlan); team-context lookups
 * read teams.plan (getTeamOwnerPlan).
 *
 * Plan tiers (Phase 3A):
 *   Free     — 1 workspace,  10 staff members, 25 docs stored,  30 AI classifications/mo
 *   Pro      — 3 workspaces, Unlimited staff,  500 docs stored, Unlimited AI
 *   Business — Unlimited workspaces, unlimited staff + docs + AI, SMS + custom branding
 */

export const PLAN_LIMITS = {
  free:     { ai: 30,     workspaces: 1,      staff_members: 10,     storage: 25      },
  pro:      { ai: 999999, workspaces: 3,      staff_members: 999999, storage: 500     },
  business: { ai: 999999, workspaces: 999999, staff_members: 999999, storage: 999999  },
};

// Full feature matrix for display (used by Billing page)
export const PLAN_FEATURES = {
  free: {
    name: 'Free',
    price_monthly: 0,
    price_annual: 0,
    features_included: [
      '1 workspace',
      'Up to 10 staff members',
      '25 documents stored',
      '30 AI classifications / month',
      'Email notifications',
      'Review workflow',
    ],
    features_excluded: [
      'Custom branding',
      'SMS notifications',
      'Priority support',
    ],
  },
  pro: {
    name: 'Pro',
    price_monthly: 29.99,
    price_annual: 290,
    features_included: [
      '3 workspaces',
      'Unlimited staff members',
      '500 documents stored',
      'Unlimited AI classifications',
      'Custom branding + logo',
      'Priority email support',
    ],
    features_excluded: [
      'SMS notifications',
    ],
  },
  business: {
    name: 'Business',
    price_monthly: 49,
    price_annual: 470,
    features_included: [
      'Unlimited workspaces',
      'Unlimited staff members',
      'Unlimited document storage',
      'Unlimited AI classifications',
      'Custom branding + logo',
      'SMS notifications',
      'Priority support',
    ],
    features_excluded: [],
  },
};

// Defensive default — if a profile row is missing or plan is null
const DEFAULT_PLAN = 'free';

/**
 * Fetch a team's effective plan (plus the owner id for usage rollups).
 *
 * Reads `teams.plan` — webhook-synced from the owner's profile and the
 * backend's enforcement source (/api/invite-member). We deliberately do
 * NOT read the owner's profiles row here: RLS only allows reading your
 * own profile, so for non-owner members that query returned nothing and
 * this helper silently fell back to free-tier limits.
 */
export async function getTeamOwnerPlan(teamId) {
  if (!teamId) return { plan: DEFAULT_PLAN, limits: PLAN_LIMITS[DEFAULT_PLAN], ownerId: null };

  const [{ data: team, error: teamErr }, { data: ownerMember }] = await Promise.all([
    supabase
      .from('teams')
      .select('plan')
      .eq('id', teamId)
      .single(),
    supabase
      .from('team_members')
      .select('user_id')
      .eq('team_id', teamId)
      .eq('is_owner', true)
      .maybeSingle(),
  ]);

  const ownerId = ownerMember?.user_id || null;

  if (teamErr || !team) {
    return { plan: DEFAULT_PLAN, limits: PLAN_LIMITS[DEFAULT_PLAN], ownerId };
  }

  const plan = (team.plan || DEFAULT_PLAN).toLowerCase();
  return {
    plan,
    limits: PLAN_LIMITS[plan] || PLAN_LIMITS[DEFAULT_PLAN],
    ownerId,
  };
}

/**
 * Fetch plan info for a specific user.
 */
export async function getUserPlan(userId) {
  if (!userId) return { plan: DEFAULT_PLAN, limits: PLAN_LIMITS[DEFAULT_PLAN], ownerId: null };

  const { data: profile, error } = await supabase
    .from('profiles')
    .select('plan, stripe_customer_id, stripe_subscription_id, plan_updated_at')
    .eq('id', userId)
    .single();

  if (error || !profile) {
    return { plan: DEFAULT_PLAN, limits: PLAN_LIMITS[DEFAULT_PLAN], ownerId: userId };
  }

  const plan = (profile.plan || DEFAULT_PLAN).toLowerCase();
  return {
    plan,
    limits: PLAN_LIMITS[plan] || PLAN_LIMITS[DEFAULT_PLAN],
    ownerId: userId,
    stripeCustomerId: profile.stripe_customer_id,
    stripeSubscriptionId: profile.stripe_subscription_id,
    planUpdatedAt: profile.plan_updated_at,
  };
}

/**
 * For a user, sum the uploads_used across every team they own.
 */
export async function getOwnerUsage(userId) {
  if (!userId) return { totalUsed: 0, teams: [] };

  const { data, error } = await supabase
    .from('team_members')
    .select('teams(id, name, primary_color, uploads_used)')
    .eq('user_id', userId)
    .eq('is_owner', true);

  if (error || !data) return { totalUsed: 0, teams: [] };

  const teams = data
    .map(d => d.teams)
    .filter(Boolean)
    .map(t => ({
      id: t.id,
      name: t.name,
      color: t.primary_color,
      used: t.uploads_used || 0,
    }));

  const totalUsed = teams.reduce((sum, t) => sum + t.used, 0);
  return { totalUsed, teams };
}

/**
 * Phase 3A: Count the number of stored documents in a team.
 * Used to enforce the Free-plan 25-doc storage cap.
 */
export async function getTeamStorageCount(teamId) {
  if (!teamId) return 0;
  const { count, error } = await supabase
    .from('document_uploads')
    .select('id', { count: 'exact', head: true })
    .eq('team_id', teamId);
  if (error) {
    console.warn('[getTeamStorageCount] failed:', error);
    return 0;
  }
  return count || 0;
}

/**
 * Phase 3A: Count STAFF members of a team (excluding clients/portal users).
 * Used to enforce the Free-plan 10-staff-member cap.
 * Clients (role slug === 'client' or team_roles.is_staff === false) don't count.
 */
export async function getTeamStaffCount(teamId) {
  if (!teamId) return 0;
  const { data, error } = await supabase
    .from('team_members')
    .select('id, team_roles(is_staff)')
    .eq('team_id', teamId);
  if (error || !data) return 0;

  // Count rows where team_roles.is_staff is true (or null, which means untyped — defensively include)
  const staffOnly = data.filter(m => m.team_roles?.is_staff !== false);
  return staffOnly.length;
}

/** Pretty label for a plan slug — use for display only */
export function planLabel(plan) {
  const p = (plan || 'free').toLowerCase();
  return p.charAt(0).toUpperCase() + p.slice(1);
}

